import {
	eventBus,
	setTitle,
	RouteApi,
	replaceComponent,
	modifyDashboardWidgets,
	modifyAssetGroupColumnDefinitions,
	setLocizeProject,
	modifyCanDeleteBundle,
	modifyCanDeleteBundleVersion,
	modifyUserGroupColumnDefinitions,
	addCustomHeaderElement,
	ThemeApi,
	AssetGroupInfo,
	WidgetDefinition,
	setEnvironment
} from '@deviceinsight/ng-ui-scale-lib/api';
import type {ReactableColumnDef} from '@deviceinsight/ng-ui-components';
import '@deviceinsight/ng-ui-components';
import favicon from './assets/favicon.png';
import {digitalTwin, floorplanWidget, info, kpiWidgets, RafiWidgetDefinition} from './components/widgets/registry';
import ruleEngineRoute from './routes/assetgroups/detail/ruleengine/routes';
import globalJobsRoute from './routes/assetgroups/detail/updatejobs/rafiadmin/global/routes';
import updateJobsRoute from './routes/assetgroups/detail/updatejobs/rafiadmin/inventory/routes';
import tenantWorkspaceJobsRoute from './routes/assetgroups/detail/updatejobs/rafitenant/workspace/routes';
import tenantInventoryJobsRoute from './routes/assetgroups/detail/updatejobs/rafitenant/inventory/routes';
import assetInfoRoute from './routes/asset/detail/info/routes';
import assetCustomDatapointsRoute from './routes/asset/detail/customDatapoints/routes';
import kisApiRoute from './routes/kisapi/routes';
import assetMgmtRoute from './routes/assetManagement/routes';
import servicestatusRoute from './routes/servicestatus/routes';
import Logo from './components/Logo';
import logo from './assets/rafi-logo-neg.png';
import UserStore from './modules/common/UserStore';
import {getUserGroupColumnDefinitions} from './usergroups/utils';
import {isBundleInUse, isBundleVersionInUse} from './components/updatejobs/common/utils';
import UserHelpButton from './components/UserHelpButton';
import hasAuthority from './components/common/hasAuthority';
import {
	AUTHORITY_READ_ASSET_DATAPOINT_APP,
	DATAPOINT_APP_RELATIVE_PATH,
	DATAPOINT_WIDGET_NAMES,
	AUTHORITY_READ_KPI_DATA_PROCESSING_DEFINITION
} from './modules/common/consts';
import dataProcessingDefinitionsAssetRoute from './routes/asset/detail/dataProcessingDefinitions/routes';
import dataProcessingDefinitionsAssetGroupRoute from './routes/assetgroups/detail/dataProcessingDefinitions/routes';
import globalDataProcessingDefinitionsRoute from './routes/portaladmin/globaldataprocessingdefinitions/routes';
import dayjs from 'dayjs';

const theme = {
	logo,
	favicon,
	primaryColor: '#003546'
};

export default class RafiCustomUiConfig {
	getTitle(): string {
		return 'KIS.MANAGER';
	}

	getLogo(): any {
		return Logo;
	}

	disableAutomaticTranslation(): void {
		const htmlRoot = document.documentElement;
		const head = document.getElementsByTagName('head')[0];
		const noTranslateMetaInfo = document.createElement('meta');
		noTranslateMetaInfo.setAttribute('name', 'google');
		noTranslateMetaInfo.setAttribute('content', 'notranslate');

		if (head) {
			head.appendChild(noTranslateMetaInfo);
		}

		if (htmlRoot) {
			htmlRoot.setAttribute('translate', 'no');
		}
	}

	addSubtenantColumnToAssetGroups(
		defaultColumnDefinitions: Array<ReactableColumnDef<AssetGroupInfo>>
	): Array<ReactableColumnDef<AssetGroupInfo>> {
		const updatedColumns = [...defaultColumnDefinitions];
		updatedColumns.splice(2, 0, {
			name: 'subtenantName',
			label: {
				id: 'properties.subtenantName',
				defaultValue: 'Customer Name'
			},
			sortable: true,
			render: ({assetGroup}: AssetGroupInfo) => assetGroup.properties['subtenantName']
		});
		return updatedColumns;
	}

	modifyCanDeleteBundleAndVersion() {
		modifyCanDeleteBundle(isBundleInUse);
		modifyCanDeleteBundleVersion(isBundleVersionInUse);
	}

	getDashboardWidgets(defaultWidgets: Array<RafiWidgetDefinition>): Array<WidgetDefinition> {
		const canSeeDatapointWidgets = hasAuthority(AUTHORITY_READ_ASSET_DATAPOINT_APP);
		const canSeeKpiWidgets = hasAuthority(AUTHORITY_READ_KPI_DATA_PROCESSING_DEFINITION);
		const visibleDefaultWidgets = defaultWidgets.filter(
			({name}) => canSeeDatapointWidgets || !DATAPOINT_WIDGET_NAMES.includes(name)
		);
		const allWidgets = [
			digitalTwin,
			floorplanWidget,
			info,
			...(canSeeKpiWidgets ? kpiWidgets : []),
			...visibleDefaultWidgets
		];
		return allWidgets as Array<WidgetDefinition>;
	}

	modifyRoutes() {
		const canSeeDatapointApp = hasAuthority(AUTHORITY_READ_ASSET_DATAPOINT_APP);

		if (!canSeeDatapointApp) {
			RouteApi.removeAssetApp(DATAPOINT_APP_RELATIVE_PATH);
		}
	}

	async go() {
		this.disableAutomaticTranslation();
		await setLocizeProject({
			projectId: import.meta.env.REACT_APP_CUSTOM_LOCIZE_PROJECT_ID,
			apiKey: import.meta.env.REACT_APP_CUSTOM_LOCIZE_API_KEY,
			version: 'latest'
		});
		this.modifyCanDeleteBundleAndVersion();
		modifyAssetGroupColumnDefinitions(this.addSubtenantColumnToAssetGroups);
		modifyUserGroupColumnDefinitions(getUserGroupColumnDefinitions);
		replaceComponent('Logo', this.getLogo());

		RouteApi.removeAssetGroupApp('conditions');

		RouteApi.addAssetGroupApp(globalJobsRoute);
		RouteApi.addAssetGroupApp(updateJobsRoute);
		RouteApi.addAssetGroupApp(tenantWorkspaceJobsRoute);
		RouteApi.addAssetGroupApp(tenantInventoryJobsRoute);
		RouteApi.addAssetGroupApp(ruleEngineRoute);
		RouteApi.addAssetGroupApp(dataProcessingDefinitionsAssetGroupRoute);

		RouteApi.addAssetApp(assetInfoRoute);
		RouteApi.addAssetApp(assetCustomDatapointsRoute);
		RouteApi.addAssetApp(dataProcessingDefinitionsAssetRoute);

		const portaladminroute = RouteApi.findRoutes('portaladmin', RouteApi.getRoutes())[0];
		const portaladminRouteChildren = portaladminroute?.childRoutes;
		portaladminRouteChildren?.push(kisApiRoute);
		portaladminRouteChildren?.push(assetMgmtRoute);
		portaladminRouteChildren?.splice(7, 0, servicestatusRoute); //place it at position 7 like before migration
		portaladminRouteChildren?.splice(2, 0, globalDataProcessingDefinitionsRoute);

		setTitle(this.getTitle());
		ThemeApi.setTheme(theme);
		ThemeApi.setUserTheme(theme);
		ThemeApi.setCustomTheme(theme);
		ThemeApi.setDefaultTheme(theme);
		eventBus.onSignin((user) => {
			if (UserStore.getLastLoggedInUser() && UserStore.getLastLoggedInUser().name !== user.name) {
				// if a different user logs in, clear the settings
				window.location.reload();
			}
			dayjs.locale(user.locale);
			UserStore.setUser(user);
			UserStore.setLastLoggedInUser(user);
			modifyDashboardWidgets(this.getDashboardWidgets);
			this.modifyRoutes();
			setTimeout(() => {
				if (!document.getElementById('user-help-button')) {
					addCustomHeaderElement('manualLink', UserHelpButton);
				}
			}, 2000);
		});
		eventBus.onSignout(() => {
			UserStore.setUser(null);

			if (ThemeApi.getUserTheme().primaryColor !== theme.primaryColor) {
				ThemeApi.setUserTheme(theme);
			}
		});
		eventBus.onUserProfileChange((user) => {
			dayjs.locale(user.locale);
		});
		addCustomHeaderElement('manualLink', UserHelpButton);

		// allows using a different key for local development
		if (import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
			setEnvironment({REACT_APP_GOOGLE_MAPS_API_KEY: import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY});
		}
	}
}
